/**
 * Main decision display card showing Scale/Fix/Kill recommendation
 */

import { useState } from 'react';

import { Card } from '@/components/ui/Card';
import { decisionColors, confidenceColors } from '@/lib/design-tokens';
import type { Decision } from '@/types';

export interface DecisionCardProps {
  decision: Decision;
  executiveSummary: string;
}

export function DecisionCard({ decision, executiveSummary }: DecisionCardProps) {
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);

  const decisionColor = decisionColors[decision.call];
  const confidenceColor = confidenceColors[decision.confidence];

  return (
    <Card className="p-6">
      {/* Header with Decision and Confidence */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-4">
          {/* Decision Badge */}
          <div
            className="px-6 py-3 rounded-full text-2xl font-black uppercase tracking-wide"
            style={{
              backgroundColor: decisionColor.bg,
              color: decisionColor.text,
            }}
          >
            {decision.call}
          </div>

          {/* Confidence Indicator */}
          <div className="flex flex-col">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Confidence
            </span>
            <span
              className="text-lg font-bold"
              style={{ color: confidenceColor }}
            >
              {decision.confidence}
            </span>
          </div>
        </div>
      </div>

      {/* Primary Reason */}
      <div className="mb-4">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
          Primary Reason
        </h3>
        <p className="text-base text-gray-900 font-medium leading-relaxed">
          {decision.primaryReason}
        </p>
      </div>

      {/* Secondary Reasons */}
      {decision.secondaryReasons.length > 0 && (
        <div className="mb-4">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
            Supporting Reasons
          </h3>
          <ul className="space-y-2">
            {decision.secondaryReasons.map((reason, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                <span className="text-gray-400 mt-0.5">•</span>
                <span className="leading-relaxed">{reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* If We Do Nothing */}
      <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-orange-700 mb-2">
          If We Do Nothing
        </h3>
        <p className="text-sm text-orange-900 leading-relaxed">
          {decision.ifWeDoNothing}
        </p>
      </div>

      {/* Executive Summary (Collapsible) */}
      <div>
        <button
          onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
          className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
            Executive Summary
          </h3>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${
              isSummaryExpanded ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {isSummaryExpanded && (
          <div className="mt-3 p-4 bg-surface border border-gray-200 rounded-lg">
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
              {executiveSummary}
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}
